import { runWithErrorReporting } from "$root/api/error-reporting/report";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import type { EntityManagementActions } from "$root/components/spawnable/entity-management/actions";
import { spawnCreateDialog } from "$root/components/spawnable/entity-management/create-dialog";
import { spawnDeleteDialog } from "$root/components/spawnable/entity-management/delete-dialog";
import { spawnDuplicateDialog } from "$root/components/spawnable/entity-management/duplicate-dialog";
import { spawnRenameDialog } from "$root/components/spawnable/entity-management/rename-dialog";
import { spawnYesNoDialog } from "$root/components/spawnable/yes-no-dialog";
import { platformToast } from "$root/notification-system/toast";
import { trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { ToastableError } from "$root/utils";
import { useMappedQuery, useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import { type SmartWorkspaceStructureDTOTypeEnum, StructureRootFolderEnum } from "@eu.mdotm.api/smart-workspaces";
import { Text } from "@mdotm/shadcn/legacy/components";
import { FormField, Select } from "@mdotm/shadcn/mdotm/components";
import { t } from "i18next";
import { useMemo, useState } from "react";
import { workspaceRepository } from "./repository";
import { flattenMapTree } from "@mdotm/utils/collections";
import { hasAccess } from "$root/components/AuthorizationGuard";
import { readAtom } from "@mdotm/shadcn/legacy/reactivity";
import { user$ } from "../user";
import { useQuery } from "@tanstack/react-query";

export type MinimumWorkspaceEntityManagementData = {
	uuid: string;
	taxonomy: string[];
	name: string;
};

export function useWorkspaceEntityManagementActions(
	workspace: MinimumWorkspaceEntityManagementData,
	callbacks?: {
		onRename?(uuid: string): void;
		onDelete?(uuid: string): void;
		onDuplicate?(uuid: string): void;
		onCreate?(uuid: string): void;
	},
): Partial<EntityManagementActions> & {
	moveToFolder?(): Promise<void>;
} {
	return useMemo(
		() => (!workspace ? {} : workspaceEntityManagementActions(/* user,  */ workspace, callbacks)),
		[callbacks, workspace /* , user */],
	);
}

export type WorkspaceEntityManagementActions = Partial<EntityManagementActions> & {
	moveToFolder?(): Promise<void>;
};

export function workspaceEntityManagementActions(
	// user: { id: string },
	workspaceMinimumInfo: MinimumWorkspaceEntityManagementData,
	callbacks?: {
		onRename?(uuid: string): void;
		onDelete?(uuid: string): void;
		onDuplicate?(uuid: string): void;
		onCreate?(uuid: string): void;
	},
): WorkspaceEntityManagementActions {
	const baseActions = {
		createAsync:
			/* !aclByArea.workspace.canCreate(user.id, workspace?.richAcl?.acl ?? []) || workspace?.status === "ERROR"
				? undefined
				:  */ () => {
				let targetPath: string[] | null =
					workspaceMinimumInfo.taxonomy?.slice(
						0,
						Math.max(1 /* always keep ROOT */, workspaceMinimumInfo.taxonomy.length - 1),
					) ?? null;
				return spawnCreateDialog({
					children: (
						<TargetPathSelector
							initialPath={targetPath ?? undefined}
							onChange={(path) => {
								targetPath = path;
							}}
						/>
					),
					entityType: "workspace",
					onSubmitAsync: async (name) => {
						return runWithErrorReporting(
							async () => {
								try {
									const workspace = await workspaceRepository.getWorkspace.fetchQuery(workspaceMinimumInfo.uuid);
									const { uuid, ...workspaceDetailsWithoutUuid } = workspace;
									const workspaceResponse = await workspaceRepository.createWorkspace({
										...workspaceDetailsWithoutUuid,
										name,
										taxonomy: targetPath ?? [StructureRootFolderEnum.Root],
									});
									trackMixPanelEvent("workspace.entityActions.create", {
										trigger: "new",
										workspace: {
											uuid: workspaceResponse.uuid!,
											name,
											taxonomy: targetPath ?? [StructureRootFolderEnum.Root],
										},
										source: { uuid: workspaceMinimumInfo.uuid, name: workspaceMinimumInfo.name },
									});
									callbacks?.onCreate?.(workspaceResponse.uuid!);
									return workspaceResponse.uuid!;
								} catch (error) {
									throw new ToastableError(t("SOMETHING_WENT_WRONG"), {
										cause: error,
										icon: "Portfolio",
									});
								}
							},
							{
								area: "portfolio",
								attemptedOperation: {
									message: "create workspace",
									payload: JSON.stringify({ workspace: workspaceMinimumInfo }),
								},
							},
						);
					},
					checkIfNameIsAvailable: workspaceRepository.isWorkspaceNameAvailable,
					header: "New Workspace",
				});
			},
		deleteAsync: !workspaceMinimumInfo.uuid
			? undefined
			: /* !aclByArea.workspace.canDelete(user.id, workspace?.richAcl?.acl ?? []) || workspace?.status === "ERROR"
				? undefined
				: */ () =>
					spawnDeleteDialog({
						entityName: workspaceMinimumInfo?.name ?? "",
						entityType: "workspace",
						noButton: "Cancel",
						yesButton: "Proceed",
						onDeleteAsync: () =>
							runWithErrorReporting(
								async () => {
									try {
										await workspaceRepository.deleteWorkspace(workspaceMinimumInfo.uuid ?? "");

										trackMixPanelEvent("workspace.entityActions.delete", {
											workspace: { uuid: workspaceMinimumInfo.uuid, name: workspaceMinimumInfo.name },
										});

										platformToast({
											children: t("PUSH_NOTIFICATION.SUCCESS_DELETE_WORKSPACE", {
												name: workspaceMinimumInfo.name,
											}),
											severity: "success",
											icon: "Portfolio",
										});
										callbacks?.onDelete?.(workspaceMinimumInfo.uuid ?? "");
									} catch (error) {
										throw new ToastableError(t("SOMETHING_WENT_WRONG"), {
											cause: error,
											icon: "Portfolio",
										});
									}
								},
								{
									area: "portfolio",
									attemptedOperation: {
										message: `delete workspace "${workspaceMinimumInfo.uuid}"`,
										payload: JSON.stringify({ workspace: workspaceMinimumInfo }),
									},
								},
							),
					}),
		duplicateAsync: /* !workspace.status || invalidStatus.includes(workspace.status)
			? undefined
			: */ () => {
			let targetPath: string[] | null =
				workspaceMinimumInfo.taxonomy?.slice(
					0,
					Math.max(1 /* always keep ROOT */, workspaceMinimumInfo.taxonomy.length - 1),
				) ?? null;
			return spawnDuplicateDialog({
				children: (
					<TargetPathSelector
						initialPath={targetPath ?? undefined}
						onChange={(path) => {
							targetPath = path;
						}}
					/>
				),
				entityType: "workspace",
				originalName: workspaceMinimumInfo?.name ?? "",
				checkIfNameIsAvailable: workspaceRepository.isWorkspaceNameAvailable,
				onSubmitAsync(name) {
					return runWithErrorReporting(
						async () => {
							try {
								const workspaceData = await workspaceRepository.getWorkspace.fetchQuery(workspaceMinimumInfo.uuid);
								const duplicatedWorkspace = await workspaceRepository.createWorkspace({
									...workspaceMinimumInfo,
									name,
									taxonomy: targetPath ?? [StructureRootFolderEnum.Root],
									charts: workspaceData.charts,
									dataProvider: workspaceData.dataProvider,
									grid: workspaceData.grid,
									parameterizedConcepts: workspaceData.parameterizedConcepts,
									predicate: workspaceData.predicate,
									viewMode: workspaceData.viewMode,
								});

								trackMixPanelEvent("workspace.entityActions.create", {
									trigger: "duplicate",
									workspace: {
										uuid: duplicatedWorkspace.uuid!,
										name,
										taxonomy: targetPath ?? [StructureRootFolderEnum.Root],
									},
									source: { uuid: workspaceMinimumInfo.uuid, name: workspaceMinimumInfo.name },
								});

								platformToast({
									children: t("PUSH_NOTIFICATION.SUCCESS_DUPLICATED_WORKSPACE", { name }),
									severity: "success",
									icon: "Portfolio",
								});

								return "";
							} catch (error) {
								throw new ToastableError(t("SOMETHING_WENT_WRONG"), {
									cause: error,
									icon: "Portfolio",
								});
							}
						},
						{
							area: "portfolio",
							attemptedOperation: {
								message: `duplicate workspace "${workspaceMinimumInfo.uuid}"`,
								payload: JSON.stringify({ workspace: workspaceMinimumInfo }),
							},
						},
					);
				},
			});
		},
		renameAsync: !workspaceMinimumInfo.uuid
			? undefined
			: /* !aclByArea.workspace.canRename(user.id, workspace?.richAcl?.acl ?? []) || workspace?.status === "ERROR"
				? undefined
				: */ () => {
					return spawnRenameDialog({
						entityType: "workspace",
						currentName: workspaceMinimumInfo?.name ?? "",
						checkIfNameIsAvailable: workspaceRepository.isWorkspaceNameAvailable,
						onSubmitAsync(name) {
							return runWithErrorReporting(
								async () => {
									try {
										if (!workspaceMinimumInfo.taxonomy) {
											throw new Error("missing taxonomy");
										}

										await workspaceRepository.renameWorkspace({
											path: workspaceMinimumInfo.taxonomy,
											newName: name,
											workspaceUuid: workspaceMinimumInfo.uuid,
											type: "WORKSPACE",
										});

										platformToast({
											children: t("PUSH_NOTIFICATION.SUCCESS_RENAMED_WORKSPACE", { name }),
											severity: "success",
											icon: "Portfolio",
										});
										callbacks?.onRename?.(workspaceMinimumInfo.uuid);

										return workspaceMinimumInfo.uuid;
									} catch (error) {
										throw new ToastableError(t("SOMETHING_WENT_WRONG"), {
											cause: error,
											icon: "Portfolio",
										});
									}
								},
								{
									area: "portfolio",
									attemptedOperation: {
										message: `rename workspace "${workspaceMinimumInfo.uuid}"`,
										payload: JSON.stringify({ workspace: workspaceMinimumInfo }),
									},
								},
							);
						},
					});
				},
	};

	return {
		...baseActions,
		moveToFolder:
			/* !aclByArea.workspace.canRename(user.id, workspace?.richAcl?.acl ?? []) || workspace?.status === "ERROR" */
			!workspaceMinimumInfo.uuid
				? undefined
				: async () => {
						let targetPath: string[] | null =
							workspaceMinimumInfo.taxonomy?.slice(
								0,
								Math.max(1 /* always keep ROOT */, workspaceMinimumInfo.taxonomy.length - 1),
							) ?? null;
						spawnYesNoDialog({
							header: "Move to folder",
							children: (
								<TargetPathSelector
									initialPath={targetPath ?? undefined}
									onChange={(path) => {
										targetPath = path;
									}}
								/>
							),
							disableYesButton: !targetPath?.length || workspaceMinimumInfo.taxonomy == null,
							async onSubmitAsync() {
								return runWithErrorReporting(
									async () => {
										try {
											await workspaceRepository.moveWorkspace({
												oldPath: workspaceMinimumInfo.taxonomy,
												newPath: targetPath!,
												workspaceUuid: workspaceMinimumInfo.uuid,
											});

											platformToast({
												children: t("PUSH_NOTIFICATION.SUCCESS_MOVED_WORKSPACE", { name: workspaceMinimumInfo.name }),
												severity: "success",
												icon: "Portfolio",
											});
										} catch (error) {
											throw new ToastableError(t("SOMETHING_WENT_WRONG"), {
												cause: error,
												icon: "Portfolio",
											});
										}
									},
									{
										area: "portfolio",
										attemptedOperation: {
											message: `moved workspace "${workspaceMinimumInfo.uuid}"`,
											payload: JSON.stringify({ workspace: workspaceMinimumInfo }),
										},
									},
								);
							},
							yesButton: "Proceed",
							noButton: "Cancel",
						});
					},
	};
}

export type WorkspaceFolderManagementActions = Omit<EntityManagementActions, "duplicateAsync">;
export function workspaceFolderManagementActions(
	workspaceFolder: { taxonomy: string[]; name: string },
	callbacks?: {
		onRename?(uuid: string): void;
		onDelete?(): void;
		onDuplicate?(uuid: string): void;
	},
): WorkspaceFolderManagementActions {
	//TODO: add user acl
	const baseAction = {
		createAsync() {
			return spawnCreateDialog({
				entityType: "workspace folder",
				onSubmitAsync: async (name) => {
					return runWithErrorReporting(
						async () => {
							try {
								await workspaceRepository.createWorkspaceFolder(name);
								trackMixPanelEvent("workspace.folderActions.create", { folder: { name } });
								return name;
							} catch (error) {
								throw new ToastableError(t("SOMETHING_WENT_WRONG"), {
									cause: error,
									icon: "Portfolio",
								});
							}
						},
						{
							area: "portfolio",
							attemptedOperation: {
								message: "create workspace folder",
								payload: JSON.stringify({ workspaceFolder }),
							},
						},
					);
				},
				checkIfNameIsAvailable: workspaceRepository.isWorkspaceNameAvailable,
				header: "New folder",
			});
		},
		renameAsync() {
			return spawnRenameDialog({
				entityType: "workspace folder",
				currentName: workspaceFolder?.name ?? "",
				checkIfNameIsAvailable: workspaceRepository.isWorkspaceNameAvailable,
				onSubmitAsync(name) {
					return runWithErrorReporting(
						async () => {
							try {
								if (!workspaceFolder.taxonomy) {
									throw new Error("missing taxonomy");
								}

								await workspaceRepository.renameWorkspace({
									path: workspaceFolder.taxonomy,
									newName: name,
									type: "FOLDER",
								});

								platformToast({
									children: t("PUSH_NOTIFICATION.SUCCESS_RENAMED_WORKSPACE_FOLDER", { name }),
									severity: "success",
									icon: "Portfolio",
								});
								callbacks?.onRename?.(name);

								return name;
							} catch (error) {
								throw new ToastableError(t("SOMETHING_WENT_WRONG"), {
									cause: error,
									icon: "Portfolio",
								});
							}
						},
						{
							area: "portfolio",
							attemptedOperation: {
								message: `rename workspace folder "${workspaceFolder.name}"`,
								payload: JSON.stringify({ workspaceFolder }),
							},
						},
					);
				},
			});
		},
		deleteAsync() {
			return spawnDeleteDialog({
				entityName: workspaceFolder?.name ?? "",
				description: (
					<Text type="Body/M/Book">
						This action will permanently delete the <Text type="Body/M/Bold">{workspaceFolder.name}</Text> folder and
						all of its workspaces.
						<br />
						Do you want to continue?
					</Text>
				),
				yesButton: "Proceed",
				noButton: "Cancel",
				entityType: "workspace folder",
				onDeleteAsync: () =>
					runWithErrorReporting(
						async () => {
							try {
								await workspaceRepository.deleteWorkspaceFolder(workspaceFolder.taxonomy);

								trackMixPanelEvent("workspace.folderActions.delete", {
									folder: { name: workspaceFolder.name, taxonomy: workspaceFolder.taxonomy },
								});

								platformToast({
									children: t("PUSH_NOTIFICATION.SUCCESS_DELETE_WORKSPACE_FOLDER", {
										name: workspaceFolder.name,
									}),
									severity: "success",
									icon: "Portfolio",
								});
								callbacks?.onDelete?.();
							} catch (error) {
								throw new ToastableError(t("SOMETHING_WENT_WRONG"), {
									cause: error,
									icon: "Portfolio",
								});
							}
						},
						{
							area: "portfolio",
							attemptedOperation: {
								message: `delete workspace folder "${workspaceFolder.name}"`,
								payload: JSON.stringify({ workspaceFolder }),
							},
						},
					),
			});
		},
	} satisfies WorkspaceFolderManagementActions;

	return baseAction;
}

type WorkspacePath = {
	path: string;
	type: SmartWorkspaceStructureDTOTypeEnum;
};

function TargetPathSelector(props: {
	onChange(path: string[] | null): void;
	initialPath?: string[];
	disabled?: boolean;
}) {
	const [target, setTarget] = useState<string[] | null>(props.initialPath ?? null);
	const user = readAtom(user$);
	const workspaceTree = useQuery({
		...workspaceRepository.tree.getQueryConfig(),
		enabled: hasAccess(user, { requiredService: "SMART_WORKSPACE" }),
	});

	const availablePathsQuery = useMappedQuery(
		(tree) =>
			flattenMapTree(
				tree,
				(node, path): Array<WorkspacePath> => [
					...path.map((ancestor) => ({ path: ancestor.name, type: ancestor.type })),
					{ path: node.name, type: node.type },
				],
				"children",
			).flatMap((path) => (path.at(-1)?.type === "FOLDER" ? [path.map((x) => x.path)] : [])),
		[],
		workspaceTree,
	);

	useQueryReactor(availablePathsQuery, {
		onSuccess(data) {
			setTarget((latestTarget) => {
				if (latestTarget === null || latestTarget.length === 0) {
					props.onChange(data.at(0) ?? null);
					return data.at(0) ?? null;
				}
				return latestTarget;
			});
		},
	});

	return (
		<FormField label="Destination">
			{({ id }) => (
				<ReactQueryWrapperBase query={availablePathsQuery}>
					{(availablePaths) => (
						<Select
							triggerDataAttrs={{ "data-qualifier": "EntityManagementDialog/Destination" }}
							id={id}
							value={target}
							options={availablePaths.map((path) => {
								const humanReadablePath = !path
									? "None"
									: (path[0] === StructureRootFolderEnum.Root ? ["Portfolios", ...path.slice(1)] : path).join(" / ");
								return { label: humanReadablePath, value: path };
							})}
							onChange={(path) => {
								setTarget(path);
								props.onChange(path);
							}}
							disabled={props.disabled}
						/>
					)}
				</ReactQueryWrapperBase>
			)}
		</FormField>
	);
}
